/**
 * console 시작 페이지.
 *
 * 로그인 후 /api/v1/console/me 호출 → console_enabled 통과 + owner 정보 + 소속 본사.
 * 본사 소속이면 본사 회사 단위 가맹점 매장 리스트(/franchise-stores) 카드 표시.
 *
 * 본사 모델 (0040): owner.headquarters_id 가 있으면 본사 소속.
 *   같은 본사 소속 owner 누가 로그인하든 같은 매장 리스트 봄.
 *
 * 본격 화면(메뉴 템플릿 작성/원가 입력)은 다음 라운드.
 */
import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, LogOut, Store, Building2, ClipboardList } from 'lucide-react';
import { SERVER_URL, STORAGE_KEY_SESSION_TOKEN, STORAGE_KEY_OWNER_ID } from '@/config';

interface ConsoleMe {
  ok: boolean;
  ownerId: string;
  phone: string;
  displayName: string;
  consoleEnabled: boolean;
  headquartersId: string | null;
  headquartersName: string | null;
}

interface FranchiseStore {
  id: string;
  name: string;
  ownerName: string;
  isActive: boolean;
  createdAt: string;
  setupStatus: string;
  franchiseeDisplayName: string | null;
  franchiseePhone: string | null;
}

function authedFetch(path: string) {
  const token = localStorage.getItem(STORAGE_KEY_SESSION_TOKEN);
  return fetch(`${SERVER_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function HomePage() {
  const nav = useNavigate();
  const [me, setMe] = useState<ConsoleMe | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEY_SESSION_TOKEN);
    if (!token) {
      nav('/login', { replace: true });
      return;
    }
    authedFetch('/api/v1/console/me')
      .then(async (res) => {
        if (res.status === 401) {
          localStorage.removeItem(STORAGE_KEY_SESSION_TOKEN);
          localStorage.removeItem(STORAGE_KEY_OWNER_ID);
          nav('/login', { replace: true });
          return null;
        }
        if (res.status === 403) {
          nav('/access-denied', { replace: true });
          return null;
        }
        if (!res.ok) throw new Error(`server ${res.status}`);
        return res.json() as Promise<ConsoleMe>;
      })
      .then((data) => {
        if (data?.ok) setMe(data);
      })
      .catch((err) => setError((err as Error).message));
  }, [nav]);

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY_SESSION_TOKEN);
    localStorage.removeItem(STORAGE_KEY_OWNER_ID);
    nav('/login', { replace: true });
  };

  if (error) {
    return <div className="p-8 text-sm text-destructive">서버 오류: {error}</div>;
  }

  if (!me) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-fg-subtler">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> 불러오는 중...
      </div>
    );
  }

  const inHeadquarters = !!me.headquartersId;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="h-14 border-b border-bdr-subtlest bg-surface flex items-center justify-between px-6">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-fg flex items-center justify-center">
            <span className="text-surface text-xs font-bold">C</span>
          </div>
          <span className="font-bold text-sm">ATGRD 설정콘솔</span>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-1.5 text-xs text-fg-subtler hover:text-fg"
        >
          <LogOut className="w-3.5 h-3.5" /> 로그아웃
        </button>
      </header>

      <main className="flex-1 p-8 max-w-4xl mx-auto w-full space-y-6">
        <header>
          <h1 className="text-xl font-bold flex items-center gap-2">
            {inHeadquarters && <Building2 className="w-5 h-5 text-fg-subtle" />}
            {me.displayName} 님, 환영합니다.
          </h1>
          <p className="text-xs text-fg-subtler mt-1">
            {me.phone} ·{' '}
            {inHeadquarters
              ? `${me.headquartersName} 본사 담당자`
              : '본사 소속 없음'}
          </p>
        </header>

        {inHeadquarters ? (
          <FranchiseStoresCard headquartersName={me.headquartersName ?? '본사'} />
        ) : (
          <section className="bg-surface border border-bdr-subtlest rounded-lg p-6">
            <h2 className="text-sm font-bold mb-2">본사 소속 없음</h2>
            <p className="text-xs text-fg-subtler leading-relaxed">
              이 계정은 본사 회사에 소속되어 있지 않습니다. 본사 담당자 권한이 필요하면
              ATGRD에 문의하세요 (purchase@autogeared.com).
            </p>
          </section>
        )}

        {/* 신규 점포 개설 절차 */}
        <Link
          to="/store-opening"
          className="block bg-surface border border-bdr-subtlest rounded-lg p-6 hover:border-bdr-subtle transition-colors"
        >
          <div className="flex items-center gap-3">
            <ClipboardList className="w-5 h-5 text-fg-subtle shrink-0" />
            <div>
              <h2 className="text-sm font-bold">신규 점포 개설 절차</h2>
              <p className="text-xs text-fg-subtler mt-0.5">
                행정 절차 종속 순서 안내 (9단계 flowchart)
              </p>
            </div>
            <span className="ml-auto text-xs text-fg-subtler">→</span>
          </div>
        </Link>

        <section className="bg-surface border border-bdr-subtlest rounded-lg p-6">
          <h2 className="text-sm font-bold mb-2">다음 라운드 예정</h2>
          <ul className="text-xs text-fg-subtler leading-relaxed list-disc pl-5 space-y-1">
            <li>본사 표준 메뉴 템플릿 작성/배포</li>
            <li>본사 표준명 ↔ 가맹점 플랫폼 등록명 화해</li>
            <li>원가 입력 + 식자재 비율 인사이트</li>
          </ul>
        </section>
      </main>
    </div>
  );
}

function FranchiseStoresCard({ headquartersName }: { headquartersName: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['console-franchise-stores'],
    queryFn: async () => {
      const res = await authedFetch('/api/v1/console/franchise-stores');
      if (!res.ok) throw new Error(`server ${res.status}`);
      return res.json() as Promise<{
        ok: boolean;
        headquartersId: string | null;
        data: FranchiseStore[];
      }>;
    },
  });

  return (
    <section className="bg-surface border border-bdr-subtlest rounded-lg p-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold flex items-center gap-1.5">
          <Store className="w-4 h-4" /> {headquartersName} 가맹점 매장
        </h2>
        <span className="text-xs text-fg-subtler">{data ? `${data.data.length}곳` : ''}</span>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-fg-subtler py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> 불러오는 중...
        </div>
      )}
      {error && (
        <div className="text-sm text-destructive py-6 text-center">
          {(error as Error).message}
        </div>
      )}
      {data && data.data.length === 0 && (
        <div className="text-sm text-fg-subtler py-6 text-center">
          아직 묶인 가맹점 매장이 없습니다. ATGRD 직원이 매장 관리에서 본사를 박아주면 여기에
          표시됩니다.
        </div>
      )}
      {data && data.data.length > 0 && (
        <ul className="divide-y divide-bdr-subtlest text-sm">
          {data.data.map((s) => (
            <li key={s.id} className="py-2.5">
              <div className="font-semibold">{s.name}</div>
              <div className="text-xs text-fg-subtler">
                <span className="font-mono">{s.id}</span>
                {s.franchiseeDisplayName && <span> · 점주 {s.franchiseeDisplayName}</span>}
                {!s.isActive && (
                  <span className="ml-2 text-destructive">[비활성]</span>
                )}
                {s.setupStatus !== 'active' && (
                  <span className="ml-2 text-amber-600">[{s.setupStatus}]</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
